import { z } from 'zod';
import type {
  ParsedEndpoint,
  ParsedWebPage,
} from '../../ToolCallsRenderers/types';

const ListedAssetSchema = z.object({
  id: z.string(),
  type: z.enum(['ENDPOINT', 'WEBPAGE']),
  host: z.string().optional(),
  path: z.string().optional(),
  url: z.string().optional(),
  method: z.string().optional(),
});

const AssetsListPayloadSchema = z.object({
  assets: z.array(z.unknown()),
  returned: z.number().optional(),
  matched: z.number().optional(),
  truncated: z.boolean().optional(),
});

export type ParsedAssetsList = {
  endpoints: ParsedEndpoint[];
  webPages: ParsedWebPage[];
  returned?: number;
  matched?: number;
  truncated?: boolean;
};

const toWebpageUrl = (
  asset: z.infer<typeof ListedAssetSchema>,
): string | undefined => {
  if (asset.url) {
    return asset.url;
  }

  if (asset.host && asset.path) {
    return `https://${asset.host}${asset.path}`;
  }

  return undefined;
};

const unwrapRecord = (content: unknown): Record<string, unknown> | null => {
  if (
    typeof content === 'object' &&
    content !== null &&
    !Array.isArray(content)
  ) {
    return content as Record<string, unknown>;
  }

  if (typeof content !== 'string') {
    return null;
  }

  const jsonStart = content.indexOf('{');
  if (jsonStart < 0) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(content.slice(jsonStart));
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      !Array.isArray(parsed)
    ) {
      return parsed as Record<string, unknown>;
    }

    return null;
  } catch {
    return null;
  }
};

export const parseAssetsListContent = (
  content: unknown,
): ParsedAssetsList | null => {
  const record = unwrapRecord(content);
  if (!record) {
    return null;
  }

  const parsed = AssetsListPayloadSchema.safeParse(record);
  if (!parsed.success) {
    return null;
  }

  const listedAssets = parsed.data.assets.flatMap((item) => {
    const asset = ListedAssetSchema.safeParse(item);
    return asset.success ? [asset.data] : [];
  });

  return {
    endpoints: listedAssets
      .filter((asset) => asset.type === 'ENDPOINT')
      .map((asset) => ({
        endpointId: asset.id,
        endpointUrl: asset.url,
        path: asset.path,
        method: asset.method,
      })),
    webPages: listedAssets
      .filter((asset) => asset.type === 'WEBPAGE')
      .map((asset) => ({
        id: asset.id,
        url: toWebpageUrl(asset),
      })),
    returned: parsed.data.returned,
    matched: parsed.data.matched,
    truncated: parsed.data.truncated,
  };
};
